define('atlas/AtlasPlayer', ['atlas/AtlasAPIImageAdapter', 'atlas/AtlasClientCapabilities', 'atlas/AtlasImageCache', 'atlas/AtlasImageLoader', 'atlas/AtlasSphere', 'atlas/AtlasSphereRefinement', 'atlas/AtlasViewerConfiguration', 'atlas/AtlasConstants', 'atlas/AtlasViewer', 'atlas/AtlasPriorityQueue', 'atlas/AtlasLoadingSequence'], function(AtlasAPIImageAdapter, AtlasClientCapabilities, AtlasImageCache, AtlasImageLoader, AtlasSphere, AtlasSphereRefinement, AtlasViewerConfiguration, Constants, AtlasViewer, AtlasPriorityQueue, AtlasLoadingSequence) {
    'use strict';

    var DEFAULTS = {
        initialView: 'I01',
        viewerTag: 'signatureImage',
        controlTag: 'controlArea',
        hideCursor: false,
        imageAdapter: new AtlasAPIImageAdapter(),
        debugFlags: Constants.LOGGING_NONE,
        loadAsConfigurator: false,
        capabilities: {
            loadingSequence: null,
            windowDimensions: {
                width: 600,
                height: 600
            },
            isRetina: false
        },
        configurationUrl: 'atlas/AtlasServerConfiguration',
        contentUrl: '',
        displayingThubmails: true
    };

    /**
    * AtlasPlayer.js
    * version: 1.4.2
    *
    * var player = new AtlasPlayer(options, asset, callback);
    *
    * Construct the atlas player by specifiying an optional options object
    *
    * options - setup and callback options, override only what you need. the following describes the option structure
    * asset - the asset structure (can be null if providing contentUrl in the options)
    * callback - callback to be triggered once the asset structure has been parsed
    *
    * usage:
    * ----------
    * To place the AtlasPlayer on a page, you will need to provide a DIV element that can be used to contain the rotating images, and a DIV that will sit above it where the users mouse actions will be detected.  If the AtlasPlayer is going to use canvas, then it will create the canvas in the appropriate location and will leave the original DIV alone.  If canvas is not going to be used, then it will use the passed in DIV by replacing the images inside of it as the user moves their mouse.
    *
    * The simplest way to get display the viewer is to name the display DIV signatureImage and the control DIV controlArea.  These are the defaults and would therefore not require any overrides passed into the options.
    *
    * Additional element may be required in order to display things like the current loading status and notifying the user when they can start interacting with the AtlasPlayer.  These are not handled directly by the player, but should be handled in the client code based on callbacks from the player.
    *
    * properties:
    * -----------
    * - `viewerTag`             defaults to 'signatureImage'.  this will be the HTML tag where the viewer will render if
    *                           it is not rendering to canvas, or will be a peer element to the canvas that is dynamically
    *                           created if rendering to canvas.
    * - `controlTag`            defaults to 'controlArea'.  this will be the HTML tag where the viewer will track mouse and
    *                           touch movements.  it should be positioned over the `viewerTag` in most cases.
    * - `dragTag`               defaults to the same area defined in controlTag.  this will be the HTML tag where the viewer
    *                           will track mouse and touch movements after the begin drag or mouse down event.  For example,
    *                           could be the entire viewable area so that the user can drag outside of the viewer window and
    *                           still be able to spin the object.
    * - `hideCursor`            defaults to true, will set cursor: none on the div defined by the drag tag on mouse down,
    *                           and remove the style on mouse up
    * - `initialView`           defaults to first preview angle or 'I01' which is the front view of the middle latitude.
    *                           this will be the angle that will be loaded first.  if passed it will try to use this angle,
    *                           if available.
    * - `loadAsConfigurator`    load the asset as if it were a configurator by applying the available latitudes to the
    *                           loading routine
    * - `imageAdapter`          defaults to a new AtlasAPIImageAdapter which will load images from v0.4 and earlier asset structures.
    * - `configurationUrl`      a url to pull the server configuration from, if not specified it will pull the default configuration
    *                           that will use multiple host names for loading
    * - `contentUrl`            url to load the asset data from
    * - `displayingThumbnails`  thumbnails are being displayed and therefore needs to priority loaded
    * - `priorityImages`        array of views that should be priority loaded for the spinner
    * - `capabilities`          a structure that includes some options to configure the interaction with the backend server
    *   - `capabilities.allowInteractivity'   default is true.  if the player will allow any interactivity at all (currently restricted for IE6)
    *   - `capabilities.rendertoCanvas`   default is true.  if the player should render to canvas, or just replace the image in the viewerTag
    *   - `capabilities.isRetina`    default is false.  if the player should support retina by attempting to load larger size images
    *   - `capabilities.windowDimensions` default is 600x600.  the width and height of the display area (passed as {width:WWW, hieght:HHH})
    *   - `capabilities.resolution`   default is 600.  the resolution of the images to load.
    *   - `capabilities.format`   default is jpg.  the format of the image to load.
    *   - `capabilities.loadingSequence`  default is null.  the loading sequence that should be used, possible options are:
    *       - `null`    use the default loading sequence of thumbnail latitudes and longitudes first, then fill in gaps
    *       - `retail`  load the latitudes on top of the object before latitudes on the bottom
    *   - `capabilities.latitudeSubset`   default is null.  an array of the latitudes that the player will be limited to.
    *   - `capabilities.loadingBailouts`  default is empty array.  an array of bailout criteria that will be tested after each loading
    *                                   stage.  see the callback for onBailout for the criteria.  note that index is an array of
    *                                   of indexes or ['*'] for all.
    * callbacks:
    * ----------
    * - `onProgress`        callback for progress events.  loading is broken down into stages, after each stage it's possible that
    *                       interactivity could be enabled (based on interactivity criteria) and/or possible that a low bandwidth
    *                       bailout could occur (based on bailout criteria).  the progress callback gets called after every image
    *                       load, and includes:
    *   - `stageTotal`                  the total number of images in the current stage
    *   - `stageCount`                  the current image of the current stage
    *   - `stagePercentage`             percentage (actually ratio not percentage) of stage loaded (stageCount/stageTotal)
    *   - `imageTotal`                  total number of images in the asset
    *   - `imageCount`                  the total number of images loaded
    *   - `imagePercentage`             percentage (actuall ratio not percentage) of total images loaded (imageCount/imageTotal)
    *   - `elapsedTime`                 time in ms since beginning of load
    *   - `estimatedTimeRemaining`      estimated amount of time left during load
    *   - `lastImage`                   JavaScript Image object of the last image loaded (get the url through the src property)
    * - `onBailout`         callback for bailout events.  this will happen only on stage boundaries, however the progress call will
    *                       happen prior to the bailout callback.  the bailout callback includes:
    *   - `index`                       the loading stage index (can be used to resume loading at that index if desired)
    *   - `totalImages`                 the total number of images loaded prior to bailout
    *   - `statistics`                  the loading statistics up to the bailout, that includes:
    *       - `times`                       individual loading times in ms for each image in the sample
    *       - `images`                      total number of images loaded being sampled
    *       - `average`                     the total time divided by the number of images
    *       - `total`                       the cumilative time for all images
    *   - `bailout`                     the bailout rule that was triggered, which includes:
    *       - `retina`                      if we are loading retina and the images are 2k
    *       - `configurator`                if the images are being loaded through as part of a configurator (on the fly compositing)
    *       - `animationEnabled`            if animation has been enabled
    *       - `index`                       the stage of loading
    *       - `interactivityChanged`        if interactivity was changed during this stage
    *       - `average`                     the average loading time for each image
    * - `onInteractivityEnabled`    callback triggered when interactivity is enabled (canvas is ready and users can rotate)
    * - `onInteractivityDisabled`   callback triggered when interactivity is disabled (configuration changed)
    * - `onAfterImageChanged`       callback triggered when the user rotates the object and the view has been updated, this includes:
    *   - `oldView`                     the previous view
    *   - `newView`                     the current view
    * - `onConfigurationChanged`    callback triggered when the user has changed the options on a configurator object, this includes:
    *   - `adapter`                     the image adapter that can be used to pull the latest composited versions of the thumbnails
    * - `errorHandler`              the error handler that should be called when an error occurs
    *   - `severity`                    the severity of the issue (5 - fatal, 0 - debug)
    *   - `message`                     a user displayable message of the error
    *
    */
function AtlasPlayer(options, asset, callback) {
        this.VERSION = '1.4.2';
        if (typeof console !== 'undefined') {
            console.log('v' + this.VERSION);
        }

        //setup the error reporting callback
        AtlasViewer.getInstance().setErrorHandler(this.onError);

        options = options || {};

        //start with the defaults
        AtlasClientCapabilities.mergeCapabilities(DEFAULTS.capabilities);

        if (options.capabilities) {
            //merge in any options passed in
            AtlasClientCapabilities.mergeCapabilities(options.capabilities);
        }
        AtlasClientCapabilities.refreshImagePreferences();

        this._viewerTag = options.viewerTag || DEFAULTS.viewerTag;
        this._controlTag = options.controlTag || DEFAULTS.controlTag;
        this._dragTag = options.dragTag || DEFAULTS.dragTag;
        this._hideCursor = (options.hideCursor!==undefined) ? options.hideCursor : DEFAULTS.hideCursor;

        //optional
        this._initialView = options.initialView || DEFAULTS.initialView;

        this._debugFlags = options.debugFlags || DEFAULTS.debugFlags;

        //load as configurator override
        this._loadAsConfigurator = options.loadAsConfigurator || DEFAULTS.loadAsConfigurator;

        //if the asset structure is passed in then we can validate the load as configurator parameter against
        //the asset structure - if the asset structure is not passed in and we are loading from the server
        //then the override will be passed in to the player and will be determined later if it cannot be loaded
        //as a configuration
        if (asset) {
            if (this._loadAsConfigurator && (!asset.customizations || !asset.customizations.length)) {
                this._loadAsConfigurator = false;
            }
        }

        //if animation is enabled
        this._animationEnabled = false;

        //default implementation is to enable interactivity only when we have enough frames and the canvas is ready
        //these state variables are only used for the internal implementation, clients that override these methods
        //cannot be relied upon to set these variables
        this._interactivityEnabled=false;
        this._canvasReady=false;

        this._displayingThumbnails = (options.displayingThumbnails !== undefined ? options.displayingThumbnails : true);

        //priority images could be elements that appear on the page that need to be clickable as soon as possible
        //for example purchased images on pixelsquid, the defualt is an empty array
        this._priorityImages = options.priorityImages !== undefined ? options.priorityImages.slice(0) : [];

        //the server configuration will either be loaded as a requirejs module if it is the default,
        //or loaded dynamically from the specified url
        this._configurationUrl = options.configurationUrl || DEFAULTS.configurationUrl;

        this._contentUrl = options.contentUrl || DEFAULTS.contentUrl;

        //set the player error handler if specified in the options
        this._errorHandler = options.errorHandler;

        //if this is the first time through to prevent marking engagement multiple times
        this._firstInteractivity = true;

        this._imageAdapter = options.imageAdapter || DEFAULTS.imageAdapter;

        this._loaded = false;

        //save the object pointer
        var that = this;
        //if no configuration url is specified, or it is equal to the default configuration module name
        //then we are going to load through requirejs, otherwise load through jquery
        if (!this._configurationUrl || (this._configurationUrl === 'atlas/AtlasServerConfiguration')) {
            require([this._configurationUrl], function(data) {
                that._serverConfiguration = data;

                if ((!asset) && (that._contentUrl)) {
                    if (!that._imageAdapter.load(that._contentUrl, that._loadAsConfigurator, that._serverConfiguration, function(object, success) {
                        if (!success) {
                            that.onError(5, 'unable to load asset information', 'retrieving.error.content_url:' + that._contentUrl);
                            return;
                        }
                        that.init(); //initialize
                        //notify our callback if we're all clear
                        if (callback) {
                            callback();
                        }
                    }, that._imageAdapter)) {
                        that.onError(5, 'unable to load asset information', 'loading.error.content_url:' + that._contentUrl);
                        return;
                    }
                }
                else if (asset) {
                    //if there is no options image adapter and the asset data is passed in the constructor, then assume that
                    //we must load the asset into the defaults
                    if (!that._imageAdapter.loadFromJSON(asset, that._loadAsConfigurator, that._serverConfiguration)) {
                        that.onError(5, 'unable to load asset information', 'loading.error.asset_json.' + asset.id);
                        return;
                    }
                    that.init(); //initialize
                    //notify our callback if we're all clear
                    if (callback) {
                        callback();
                    }
                }
                else {
                    that.onError(5, 'unable to load asset information', 'no asset information specified');
                    return;
                }
            });
        }
        else if (AtlasClientCapabilities.getCapabilities().jqueryAvailable) {
            jQuery.getJSON(this._configurationUrl, function(data) {
                that._serverConfiguration = data;

                if ((!asset) && (that._configurationUrl)) {
                    if (!that._imageAdapter.load(that._contentUrl, that._loadAsConfigurator, that._serverConfiguration, function(object, success) {
                        if (!success) {
                            that.onError(5, 'unable to load asset information', 'retrieving.error.content_url:' + that._contentUrl);
                            return;
                        }
                        that.init(); //initialize
                        //notify our callback if we're all clear
                        if (callback) {
                            callback();
                        }
                    }, that._imageAdapter)) {
                        that.onError(5, 'unable to load asset information', 'loading.error.content_url:' + that._contentUrl);
                        return;
                    }
                }
                else if (asset) {
                    //if there is no options image adapter and the asset data is passed in the constructor, then assume that
                    //we must load the asset into the defaults
                    if (!that._imageAdapter.loadFromJSON(asset, that._loadAsConfigurator, that._serverConfiguration)) {
                        that.onError(5, 'unable to load asset information', 'loading.error.asset_json.' + asset.id);
                        return;
                    }
                    that.init(); //initialize

                    //notify our callback if we're all clear
                    if (callback) {
                        callback();
                    }
                }
                else {
                    that.onError(5, 'unable to load asset information', 'no asset information specified');
                    return;
                }
            }).fail(function() {
                that.onError(5, 'configuration loading failed', 'loading.error.configuration_json');
                return;
            });
        }
        else {
            that.onError(5, 'configuration could not be loaded, jquery was not available', 'loading.error.configuration_nojquery');
            return;
        }

        //onAnimationEnabledChanged
        var self=this;
        var onAnimationEnabledChanged = function (enableInteractivity) {
            if ((enableInteractivity)&&(!self._interactivityEnabled)) {
                if (self._firstInteractivity) {
                    self._firstInteractivity = false;
                }

                if (self._canvasReady) {
                    self.enableInteractivity();
                    self._onInteractivityEnabled.call(self);
                }
            }
            else if ((!enableInteractivity)&&(self._interactivityEnabled)) {
                self._onInteractivityDisabled.call(self);
            }

            self._interactivityEnabled=enableInteractivity;
        };
        this.setOnAnimationEnabledChanged(options.onAnimationEnabledChanged || onAnimationEnabledChanged);

        //onCanvasReady
        var onCanvasReady = function() {
            //if canvas was already ready, then just return
            if (self._canvasReady) {
                return;
            }

            self._canvasReady=true;

            if (self._interactivityEnabled) {
                self.enableInteractivity();
                self._onInteractivityEnabled.call(self);
            }
        };
        this.setOnCanvasReady(options.onCanvasReady || onCanvasReady);

        //onInteractivityEnabled
        var onInteractivityEnabled = function() {
        };
        this._onInteractivityEnabled = options.onInteractivityEnabled || onInteractivityEnabled;

        //onStopInteractivity
        var onInteractivityDisabled = function() {
        };
        this._onInteractivityDisabled = options.onInteractivityDisabled || onInteractivityDisabled;

        //onBailout
        var onBailout = function(index, totalImages, statistics, bailout) {
            return true;
        };
        this.setOnBailout(options.onBailout || onBailout);

        //onAfterImageChanged
        this.setOnAfterImageChanged(options.onAfterImageChanged);
        //onConfigurationChanged
        this.setOnConfigurationChanged(options.onConfigurationChanged);
        //onProgress
        this.setOnProgress(options.onProgress);

        this._filesAvailable = false;
        this._downloadFiles = [];
        //thumbnails
        this._previews = [];
        this._shotPreviews = [];
        this._downloadTimes = [];
        this._configurationChanged = false;
        this._onOneTimeLoadImageIndex = [];

        //images that are loaded out of band
        this._outOfBand = {
            latitudes: [],
            longitudes: []
        };
    }

    AtlasPlayer.prototype.init = function() {
        if (AtlasClientCapabilities.getCapabilities().allowInteractivity === false) {
            return;
        }

        //this should be cleaned up
        this._configuration = new AtlasViewerConfiguration(this._viewerTag, this._controlTag, this._dragTag, this._hideCursor);
        this._configuration._thresholdType = Constants.THRESHOLD_ADAPTIVE;

        //if an initial view was set in the options, then we will use that, otherwise we are going to pick
        //the first preview view, otherwise we are going to pick closest to I01, otherwise I01
        if (this._initialView) {
            this._configuration._initialImageIndex = this._initialView;
        }
        else if (this._imageAdapter && this._imageAdapter.getPreviewImages() &&
            (this._imageAdapter.getPreviewImages().length > 1) && this._imageAdapter.getPreviewImages()[0].view) {
            this._configuration._initialImageIndex = this._imageAdapter.getPreviewImages()[0].view;
        }
        else if (this._imageAdapter) {
            this._configuration._initialImageIndex = this._imageAdapter.getValidImageIndex('I01');
        }
        else {
            this._configuration._initialImageIndex = 'I01';
        }

        //make copies of the preview images
        if (this._displayingThumbnails) {
            if ((this._imageAdapter.getPreviewImages()) && (this._imageAdapter.getPreviewImages().length)) {
                this._previews = this._imageAdapter.getPreviewImages().slice(0);
            }

            if ((this._imageAdapter.getStatic2dPreviewImages()) && (this._imageAdapter.getStatic2dPreviewImages().length)) {
                this._static2dPreviews = this._imageAdapter.getStatic2dPreviewImages().slice(0);
            }

            if ((this._imageAdapter.getShotPreviewImages()) && (this._imageAdapter.getShotPreviewImages().length)) {
                this._shotPreviews = this._imageAdapter.getShotPreviewImages().slice(0);
            }
        }

        this._imageCache = new AtlasImageCache();
        this._queue = new AtlasPriorityQueue(this._imageAdapter, Constants.BATCH_SIZE, null);
        this._sphere = new AtlasSphere();
        this._refinement = new AtlasSphereRefinement(this._sphere);
        this._loader = new AtlasImageLoader();
        this._sequence = new AtlasLoadingSequence();
    };

    AtlasPlayer.prototype.getCapabilities = function() {
        return AtlasClientCapabilities.getCapabilities();
    };

    AtlasPlayer.prototype.getImageAdapter = function() {
        return this._imageAdapter;
    };

    AtlasPlayer.prototype.isConfigurator = function() {
        return (this._imageAdapter._configuration && this._imageAdapter._configuration.length > 0);
    };

    AtlasPlayer.prototype.getConfiguration = function() {
        var i = 0, //loop iterator
            output = []; //configuration copy

        for (i = 0; i < this._imageAdapter._configuration.length; i++) {
            output.push({
                option: this._imageAdapter._configuration[i].option,
                choice: this._imageAdapter._configuration[i].choice
            });
        }

        //need to make a copy of the configuration when it gets returned, so that changes
        //are not immediately effected
        return output;
    };

    AtlasPlayer.prototype.getCurrentImageIndex = function() {
        return AtlasViewer.getInstance().getCurrentImageIndex();
    };

    AtlasPlayer.prototype.updateConfiguration = function(configuration) {
        //cancel loading and rendering for right now
        this._loader.cancel();
        AtlasViewer.getInstance().stopRendering();

        var viewer = AtlasViewer.getInstance();
        viewer.disableControls();

        this._imageAdapter.applyConfiguration(configuration);

        this._configurationChanged = true;
        //reset the loading statistics
        this._downloadTimes = [];

        var capabilities = AtlasClientCapabilities.getCapabilities();

        if (capabilities.forceRefreshDuration !== 0) {
            viewer.setForceCanvasRefresh(true);

            //timing issue, force refresh after initial render
            if (capabilities.forceRefreshDuration > 0) {
                setTimeout(function() {
                    viewer.setForceCanvasRefresh(false);
                }, capabilities.forceRefreshDuration);
            }
        }

        //notify any listeners that the configuration is updated
        if (this._onConfigurationChanged) {
            this._onConfigurationChanged.call(this, this._imageAdapter);
        }

        //dump the cache
        this._imageCache = new AtlasImageCache();
        this._queue = new AtlasPriorityQueue(this._imageAdapter, Constants.BATCH_SIZE, null);
        this._sphere = new AtlasSphere();
        this._refinement = new AtlasSphereRefinement(this._sphere);

        this.load();
    };

    AtlasPlayer.prototype.getVersion = function() {
        return this.VERSION;
    };

    AtlasPlayer.prototype.getImagePath = function(view) {
        return this._imageAdapter.getImagePath(view);
    };

    AtlasPlayer.prototype.getPreviewImages = function() {
        return this._imageAdapter.getPreviewImages();
    };

    AtlasPlayer.prototype.getStatic2dPreviewImages = function() {
        return this._imageAdapter.getStatic2dPreviewImages();
    };

    AtlasPlayer.prototype.getShotPreviewImages = function() {
        return this._imageAdapter.getShotPreviewImages();
    };

    AtlasPlayer.prototype.setErrorHandler = function(callback) {
        this._errorHandler = callback;
    };

    AtlasPlayer.prototype.setOnConfigurationChanged = function(callback) {
        this._onConfigurationChanged = callback;
    };

    AtlasPlayer.prototype.setOnProgress = function(callback) {
        this._onProgress = callback;
    };

    AtlasPlayer.prototype.setOnBeforeMouseMove = function(callback) {
        AtlasViewer.getInstance().setOnBeforeMouseMove(callback);
    };

    AtlasPlayer.prototype.setOnBeforeTouchMove = function(callback) {
        AtlasViewer.getInstance().setOnBeforeTouchMove(callback);
    };

    AtlasPlayer.prototype.setOnBeforeMouseDown = function(callback) {
        AtlasViewer.getInstance().setOnBeforeMouseDown(callback);
    };

    AtlasPlayer.prototype.setOnBeforeTouchDown = function(callback) {
        AtlasViewer.getInstance().setOnBeforeTouchDown(callback);
    };

    AtlasPlayer.prototype.setOnBeforeMouseUp = function(callback) {
        AtlasViewer.getInstance().setOnBeforeMouseUp(callback);
    };

    AtlasPlayer.prototype.setOnBeforeMouseOut = function(callback) {
        AtlasViewer.getInstance().setOnBeforeMouseOut(callback);
    };

    AtlasPlayer.prototype.setOnBeforeTouchEnd = function(callback) {
        AtlasViewer.getInstance().setOnBeforeTouchEnd(callback);
    };

    AtlasPlayer.prototype.setOnAfterImageChanged = function(callback) {
        AtlasViewer.getInstance().setOnAfterImageChanged(callback);
    };

    AtlasPlayer.prototype.setOnAnimationEnabledChanged = function(callback) {
        this._onAnimationEnabledChanged = callback;
    };

    AtlasPlayer.prototype.setOnBailout = function(callback) {
        this._onBailout = callback;
    };

    AtlasPlayer.prototype.setOnOneTimeRenderImageIndex = function(imageIndex, object, callback) {
        AtlasViewer.getInstance().setOnOneTimeRenderImageIndex(imageIndex, object, callback);
    };

    AtlasPlayer.prototype.setOnOneTimeLoadImageIndex = function(imageIndex, object, callback) {
        this._onOneTimeLoadImageIndex[imageIndex] = {
            object: object,
            callback: callback
        };
    };

    AtlasPlayer.prototype.setOnCanvasReady = function(callback) {
        AtlasViewer.getInstance().setOnCanvasReady(callback);
    };

    AtlasPlayer.prototype.drag = function(clientX, clientY) {
        AtlasViewer.getInstance().controlsClickDrag(clientX, clientY);
    };

    AtlasPlayer.prototype.enableInteractivity = function() {
        AtlasViewer.getInstance().enableInteractivity();
    };

    AtlasPlayer.prototype.disableInteractivity = function() {
        AtlasViewer.getInstance().disableInteractivity();
    };

    AtlasPlayer.prototype.isInteractivityEnabled = function() {
        return this._interactivityEnabled;
    };

    AtlasPlayer.prototype.onError = function(severity, message, stat) {
        if (this._errorHandler) {
            this._errorHandler(severity, message);
        }
    };

    AtlasPlayer.prototype.load = function(view) {
        this._loaded = true;

        if (AtlasClientCapabilities.getCapabilities().allowInteractivity === false) {
            return;
        }

        //clear out any image subscribers and progress subscribers we have (could be a reload)
        if (this._loader) {
            this._loader.clearImageSubscribers();
        }
        if (this._sequence) {
            this._sequence.clearProgressSubscribers();
        }

        //pick a valid initial angle based on the current image and adapter
        if (view) {
            if (!this._configuration) {
                this.init();
            }
            this._configuration._initialImageIndex = this._imageAdapter.getValidImageIndex(view);
        }
        else if (AtlasViewer.getInstance() && AtlasViewer.getInstance().getCurrentImageIndex()) {
            this._configuration._initialImageIndex = AtlasViewer.getInstance().getCurrentImageIndex();
        }
        else {
            this._configuration._initialImageIndex = this._imageAdapter.getValidImageIndex(this._configuration._initialImageIndex);
        }

        //if animation was previously enabled, then disable, and fire the change
        if (this._animationEnabled) {
            this._animationEnabled = false;
            if (this._onAnimationEnabledChanged) {
                this._onAnimationEnabledChanged.call(this, this._animationEnabled);
            }
        }

        this._outOfBand.latitudes = [];
        this._outOfBand.longitudes = [];

        this._loader.addImageSubscriber(function(img, object) {
            //cap the window to the constant that defines the analysis window
            object._downloadTimes.push({
                index: img.index,
                elapsedTime: img.elapsedTime
            });
            if (object._downloadTimes.length > Constants.LOADING_ANALYSIS_WINDOW) {
                object._downloadTimes.splice(0, 1);
            }

            if ((object._onOneTimeLoadImageIndex) && (object._onOneTimeLoadImageIndex[img.index])) {
                object._onOneTimeLoadImageIndex[img.index].callback(object._onOneTimeLoadImageIndex[img.index].object, img.index);
                object._onOneTimeLoadImageIndex[img.index] = null;
            }
        }, this);

        //add the current view latitude and longitude to the out of band list if we are currently viewing an item,
        //this should only occur on reload when changing configuration
        if (AtlasViewer.getInstance() && AtlasViewer.getInstance().getCurrentImageIndex() && this._configurationChanged) {
            var latitude = AtlasViewer.getInstance().getCurrentImageIndex().substring(0, 1);
            var longitude = AtlasViewer.getInstance().getCurrentImageIndex().substring(1);
            this._outOfBand.latitudes.push(latitude);
            this._outOfBand.longitudes.push(longitude);
        }
        else if (this._configuration._initialImageIndex) {
            var latitude = this._configuration._initialImageIndex.substring(0, 1);
            var longitude = this._configuration._initialImageIndex.substring(1);
            this._outOfBand.latitudes.push(latitude);
            this._outOfBand.longitudes.push(longitude);
        }

        if (this._priorityImages && this._priorityImages.length > 0) {
            for (var p = 0; p < this._priorityImages.length; ++p) {
                var latitude = this._priorityImages[p].substring(0, 1);
                var longitude = this._priorityImages[p].substring(1);
                if (this._outOfBand.latitudes.indexOf(latitude) < 0) {
                    this._outOfBand.latitudes.push(latitude);
                }
                if (this._outOfBand.longitudes.indexOf(longitude) < 0) {
                    this._outOfBand.longitudes.push(longitude);
                }
            }
        }

        //initial refinement is the out of band images
        if (this._previews) {
            this._previews = this._imageAdapter.getPreviewImages().slice(0);

            //split the lats and longs
            for (var p = 0; p < this._previews.length; ++p) {
                var latitude = this._previews[p].view.substring(0, 1);
                var longitude = this._previews[p].view.substring(1);

                //add to the out of band list if not already in there
                if (this._outOfBand.latitudes.indexOf(latitude) < 0) {
                    this._outOfBand.latitudes.push(latitude);
                }
                if (this._outOfBand.longitudes.indexOf(longitude) < 0) {
                    this._outOfBand.longitudes.push(longitude);
                }
            }
            //sort the lats and longs
            this._outOfBand.latitudes.sort();
            this._outOfBand.longitudes.sort();
        }

        //if we don't have a full atlas sphere, then we are just going to sequentially load
        var replicatePoles = true;

        var capabilities = AtlasClientCapabilities.getCapabilities();

        if (capabilities.loadingSequence === 'retail') {
            if (this._imageAdapter.getValidLatitudes().length < 14) {
                this._customLoadingSequence = this._sequence.getXx16RetailSequence(this._imageAdapter.getValidLatitudes(),
                    this._outOfBand, Constants.MAX_LONGITUDE_SPAN_FOR_ANIMATION);
            }
            else {
                this._customLoadingSequence = this._sequence.get16x16RetailSequence(this._outOfBand,
                    Constants.MAX_LONGITUDE_SPAN_FOR_ANIMATION);
            }
        }
        else {
            if (this._imageAdapter.getValidLatitudes().length < 14) {
                this._customLoadingSequence = this._sequence.getXx16PrioritySequence(this._imageAdapter.getValidLatitudes(),
                    this._outOfBand, Constants.MAX_LONGITUDE_SPAN_FOR_ANIMATION);
            }
            else {
                this._customLoadingSequence = this._sequence.get16x16PrioritySequence(this._outOfBand,
                    Constants.MAX_LONGITUDE_SPAN_FOR_ANIMATION);
            }
        }


        this._sequence.addProgressSubscriber(function(progress, object) {
            //only log first and every 8th
            var logFirst = (object._serverConfiguration.logFirst !== undefined ? object._serverConfiguration.logFirst : true);
            var logDivisibleBy = object._serverConfiguration.logDivisibleBy || 8;

            if (object._onProgress) {
                object._onProgress.call(this, progress);
            }
        }, this, this._loader, this._customLoadingSequence);

        this._sphere.init(this._imageAdapter.getLatitudes(), this._imageAdapter.getLongitudes(), replicatePoles);
        this.refine(0, this, this._configurationChanged);
    };

    AtlasPlayer.prototype.loadFromJSON = function (content) {
        return this._imageAdapter.loadFromJSON(content);
    };

    AtlasPlayer.prototype.getLoadingStatistics = function() {
        var cumilativeTime = 0;
        for (var i = 0; i < this._downloadTimes.length; ++i) {
            cumilativeTime += this._downloadTimes[i].elapsedTime;
        }

        var statistics = {
            times: this._downloadTimes.slice(0),
            images: this._downloadTimes.length,
            average: cumilativeTime / this._downloadTimes.length,
            total: cumilativeTime
        };

        return statistics;
    };

    AtlasPlayer.prototype.getDownloadFormatsForView = function(view) {
        return this._imageAdapter.getDownloadFormatsForView(view);
    };

    AtlasPlayer.prototype.isValidDownloadForView = function(view, format, resolution) {
        var formats = this.getDownloadFormatsForView(view);
        if (!formats.length) {
            return false;
        }

        for (var f = 0; f < formats.length; ++f) {
            var fmt = formats[f].format;
            var res = formats[f].resolution;

            if ((fmt === format) && (res === resolution)) {
                return true;
            }
        }

        return false;
    };

    AtlasPlayer.prototype.getDownloadFormatsForShot = function(tag) {
        return this._imageAdapter.getDownloadFormatsForShot(tag);
    };

    AtlasPlayer.prototype.isValidDownloadForShot = function(tag, format, resolution) {
        return this._imageAdapter.isValidDownloadForShot(tag, format, resolution);
    };

    AtlasPlayer.prototype.rotateTo = function(view) {
        var viewer = AtlasViewer.getInstance();

        if (viewer._sphere) {
            viewer.animateScrollToImageIndex(view);
        }
        else {
            this._configuration._initialImageIndex = view;
        }
    };

    AtlasPlayer.prototype.snapTo = function(view) {
        var viewer = AtlasViewer.getInstance();

        //if the viewer has already been initialized, then rotate in the viewer, otherwise set the initial configuration image
        if (viewer._sphere) {
            viewer.snapToImageIndex(view);
        }
        else {
            this._configuration._initialImageIndex = view;
        }
    };

    AtlasPlayer.prototype.refine = function(index, object, reinit) {
        var viewer = AtlasViewer.getInstance();

        if ((index >= object._customLoadingSequence.length) || (object._customLoadingSequence[index].name === '')) {
            return;
        }

        object._refinement.refine(object._customLoadingSequence[index].latitudes, object._customLoadingSequence[index].longitudes);

        object._queue.createBatches(object._refinement.getRefinedImageList());

        object._loader.addCompletedSubscriber(function(data) {
            if ((index === 0) && (!reinit)) {
                var capabilities = AtlasClientCapabilities.getCapabilities();

                //force refresh in canvas for a few seconds to make sure initial image is rendered
                if (capabilities.forceRefreshDuration !== 0) {
                    AtlasViewer.getInstance().setForceCanvasRefresh(true);

                    //timing issue, force refresh after initial render
                    if (capabilities.forceRefreshDuration > 0) {
                        setTimeout(function() {
                            AtlasViewer.getInstance().setForceCanvasRefresh(false);
                        }, capabilities.forceRefreshDuration);
                    }
                }

                viewer.init(object._configuration, object._sphere, object._imageCache);
            } else if (index === 0) {
                //force refresh is handled here by the configurator code
                viewer.reinit(object._sphere, object._imageCache);
            }

            object._loader.clearCompletedSubscribers();
            object._refinement.applyRefinements();

            //see if the animation is changing
            var animationChanged = (object._animationEnabled !== object._customLoadingSequence[index].animationEnabled);

            var stopLoading = false;

            //only test for bailouts if a bailout callback is defined
            if (object._onBailout) {
                var capabilities = AtlasClientCapabilities.getCapabilities();
                //determine if we need to bail out
                var configurator = object.isConfigurator();
                var bailouts = capabilities.loadingBailouts;
                for (var b = 0; b < bailouts.length; ++b) {
                    var bailout = bailouts[b];
                    var statistics = object.getLoadingStatistics();

                    //check if retina and we are loading 2ks (don't worry too much about 600s on retina)
                    if (
                    (!bailout.retina || (((bailout.retina === capabilities.isRetina) && (capabilities.resolution === '2k')) || (!capabilities.isRetina))) &&
                        (!bailout.configurator || (bailout.configurator === configurator)) &&
                        (!bailout.animationEnabled || (bailout.animationEnabled === object._customLoadingSequence[index].animationEnabled)) &&
                        (!bailout.index || (bailout.index.indexOf('*') >= 0) || (bailout.index.indexOf(index.toString()) >= 0)) &&
                        (!bailout.interactivityChanged || (bailout.interactivityChanged === animationChanged)) &&
                        (statistics.average >= bailout.averageImageTime)) {
                        if (!object._onBailout.call(this, index, object._sphere.getUniqueSphereCellCount(), statistics, bailout)) {
                            stopLoading = true;
                            break;
                        }
                    }
                }
            }

            //enable
            viewer.setTopLatitude(object._refinement.getTopLatitudeRefinement());
            viewer.setBottomLatitude(object._refinement.getBottomLatitudeRefinement());

            //update available latitudes and longitudes
            for (var i = 0; i < object._customLoadingSequence[index].latitudes.length; ++i) {
                viewer.enableLatitude(object._customLoadingSequence[index].latitudes[i]);
            }
            for (var i = 0; i < object._customLoadingSequence[index].longitudes.length; ++i) {
                viewer.enableLongitude(object._customLoadingSequence[index].longitudes[i]);
            }

            //fire the animation changed event
            if ((object._onAnimationEnabledChanged) && (animationChanged)) {
                object._animationEnabled = object._customLoadingSequence[index].animationEnabled;
                object._onAnimationEnabledChanged.call(object, object._animationEnabled);
                //if the animation is now enabled, then force the initial image again
                if (object._animationEnabled) {
                    viewer.setNextImageIndex(object._initialView);
                }
            }
            viewer.setAnimationEnabled(object._customLoadingSequence[index].animationEnabled);

            //set the next image
            if (object._animationEnabled) {
                viewer.enableControls();
                viewer.resumeRendering();
            }

            viewer.setNextImageIndex(viewer.getCurrentMappedImageIndex());

            //initial load so force an initial view
            if (AtlasViewer.getInstance() && !AtlasViewer.getInstance().getCurrentImageIndex()) {
                viewer.setNextImageIndex(object._initialView);
            }

            //if the bailout returns false, null or undefined, then we stop loading, if it returns true then just continue
            if (stopLoading) {
                return;
            }

            object.refine(index + 1, object, false);
        }, object._customLoadingSequence[index].name);

        object._loader.load(object._queue, object._imageCache);
    };

    AtlasPlayer.prototype.resumeLoading = function(index) {
        //begin loading at the next sequence
        this.refine(index + 1, this, false);
    };

    AtlasPlayer.prototype.getControlAdapter = function() {
        return AtlasViewer.getInstance().getControlAdapter();
    };

    AtlasPlayer.prototype.getTopLatitude = function() {
        return AtlasViewer.getInstance().getTopLatitude();
    };

    AtlasPlayer.prototype.getBottomLatitude = function() {
        return AtlasViewer.getInstance().getBottomLatitude();
    };

    AtlasPlayer.prototype.isLoaded = function() {
        return this._loaded;
    };

    AtlasPlayer.prototype.resizeWindow = function(windowSize) {
        var viewer = AtlasViewer.getInstance();
        var ratio = viewer.getCanvasRatio();

        viewer._viewerElement.width = windowSize * ratio;
        viewer._viewerElement.height = windowSize * ratio;
        $(viewer._viewerElement).css('width', windowSize);
        $(viewer._viewerElement).css('height', windowSize);
     };

    return AtlasPlayer;
});
